import { useEffect } from 'react'
import queryString from 'query-string'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { ALL_COMPANIES_OPTION } from 'utils/partnerUtils'
import { URL_CONST } from 'utils/urlConsts'

import { selectCompanySelectorObject, selectSortedCompaniesOptions } from 'containers/App/selectors'
import { companyFilterChange } from 'containers/PartnerOptions/actions'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'

/**
 * Pages that support "All Companies" selection in the top-bar company selector.
 *
 * Source of truth is CURRENT PROD (old-nav) behavior, verified live on
 * members.staging.ironscales.io (old nav, MSP session, 2026-08-13): the only
 * surfaces offering "All Companies" are the pages whose old sidebar/inline
 * selector included it — the Resolution Center area (elements/menu with the
 * `allCompaniesOption` prop), the Awareness Center (same Menu + prop), and
 * ThreatLog (its own filter, which even DEFAULTS to All Companies). No
 * /dashboard page ever offers it: the old Dashboard Menu omits the prop and
 * reverts a leaked All Companies filter to the own company with a toast, and
 * NarrativeDashboard keeps that guard for the home page.
 *
 * Each entry is a path prefix — any route that starts with this prefix
 * is considered "supporting". Add new entries here when additional pages
 * opt in; no component change required.
 */
export const ALL_COMPANIES_SUPPORTED_PREFIXES = [
  // ThreatLog (`/threat-activity`) historically supported All Companies via its
  // inline selector. The ATO sub-route (`/threat-activity/account-investigation`)
  // is excluded in `useAllCompaniesSupported` — legacy never offered All
  // Companies there (IC-49684 #18).
  URL_CONST.investigation.threatLog, // /threat-activity
  // The whole Resolution Center area: the old nav rendered ONE Menu with
  // allCompaniesOption for every /irontraps page (incidents, history, scanback —
  // resolutionCenter/index.js), so the top-bar selector carries it area-wide.
  URL_CONST.incidents.self, // /irontraps (incidents/history/scanback/attackdetails)
  // Awareness Center: old nav Menu with allCompaniesOption
  // (awareness-center/index.js), verified live on staging old-nav.
  URL_CONST.awarenessCenter.self, // /awareness-center
]

/**
 * Returns true when the current route supports the "All Companies" option.
 *
 * Route-change reactive: re-evaluates on every navigation, not just mount.
 */
export function useAllCompaniesSupported() {
  const { pathname } = useLocation()
  // ATO Users lives under the ThreatLog prefix but never accepted All Companies
  // in the old nav — keep the option off that page (and fall back if selected).
  if (pathname.startsWith(URL_CONST.investigation.ato)) return false
  return ALL_COMPANIES_SUPPORTED_PREFIXES.some((prefix) => pathname.startsWith(prefix))
}

/**
 * Pages where company switching is NOT handled at all — their data is scoped
 * server-side to the authenticated session and ignores the company filter
 * (audited in IC-48828: Deepfake Meetings/Profiles). Per product, the
 * top-bar selector is rendered DISABLED with a tooltip on these pages rather
 * than letting users pick a company that won't change anything.
 *
 * /psima is company-scoped and is not listed here.
 *
 * Each entry is a path prefix; add new entries here as pages opt out. NOTE:
 * Deepfake *Settings* lives under /settings (company-scoped) so /deepfake here
 * only matches the Meetings/Profiles routes.
 */
export const COMPANY_SCOPE_UNSUPPORTED_PREFIXES = [
  URL_CONST.deepfake.self, // /deepfake (Meetings Dashboard + Protected Profiles)
]

/**
 * Returns true when the current route honors the company filter (i.e. switching
 * company actually re-scopes its data). False on the unsupported pages above.
 *
 * Route-change reactive.
 */
export function useCompanyScopeSupported() {
  const { pathname } = useLocation()
  return !COMPANY_SCOPE_UNSUPPORTED_PREFIXES.some((prefix) => pathname.startsWith(prefix))
}

/**
 * Side-effect hook: when the user navigates FROM a page that supports
 * All Companies TO one that does not, and the current selection is
 * All Companies, fall back to the signed-in user's OWN company (the
 * "actual" company, same object the selector seeds with on mount) — not
 * whichever company happens to sort first. Falling back to the roster head
 * would silently drop the user into an unrelated tenant's data. The sorted
 * roster is only a last resort if the self company isn't in the roster.
 *
 * Must be mounted once inside TopBarCompanySelector (which is always
 * in the tree as long as the partner is present).
 */
export function useFallbackOnUnsupportedRoute() {
  const dispatch = useDispatch()
  const { pathname, search } = useLocation()
  const companyFilter = useSelector(selectCompanyFilter)
  const companies = useSelector(selectSortedCompaniesOptions)
  // selectCompanySelectorObject builds a fresh object per call, so subscribe to
  // its primitives instead — an object identity here would re-render on every
  // dispatch and churn the effect deps below.
  const selfCompanyId = useSelector((state) => selectCompanySelectorObject(state).value)
  const selfCompanyLabel = useSelector((state) => selectCompanySelectorObject(state).label)
  const supported = useAllCompaniesSupported()
  // A companyFilter in the URL already names the tenant. Do not replace it.
  const urlNamesCompany = Number(queryString.parse(search).companyFilter) > 0

  const isAllCompaniesSelected =
    !companyFilter || companyFilter.value === ALL_COMPANIES_OPTION.value

  useEffect(() => {
    if (urlNamesCompany) return
    if (!supported && isAllCompaniesSelected) {
      const target = selfCompanyId
        ? { value: selfCompanyId, label: selfCompanyLabel }
        : companies[0]
      if (target) {
        dispatch(companyFilterChange({ value: target.value, label: target.label }))
      }
    }
    // Fires on route change (pathname) AND once the company roster becomes
    // available (companies.length) — the latter covers deep-linking straight
    // onto an unsupported page before companies have loaded, which a
    // pathname-only dep would miss. Guarded by isAllCompaniesSelected, so once
    // a single company is applied the effect no-ops and won't fight the user.
  }, [
    pathname,
    search,
    urlNamesCompany,
    supported,
    isAllCompaniesSelected,
    selfCompanyId,
    selfCompanyLabel,
    companies,
    dispatch,
  ])
}
